import React, { useState } from 'react';
import { Icon } from '@iconify/react';
import axios from 'axios';
import CONFIG from '../../config';
import Swal from 'sweetalert2';
import { motion } from 'framer-motion';

const LoadingSpinner = ({ message = 'Loading Profile...' }) => (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '100px 0', width: '100%' }}>
        <motion.div
            animate={{ rotate: 360 }}
            transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
            style={{ width: '40px', height: '40px', border: '4px solid #f3f3f3', borderTop: '4px solid var(--primary)', borderRadius: '50%' }}
        />
        {message && <p style={{ marginTop: '16px', fontWeight: 900, color: 'var(--primary)', fontSize: '11px', letterSpacing: '1px' }}>{message.toUpperCase()}</p>}
    </div>
);

const Profile = ({ user: initialUser }) => {
    const [user, setUser] = useState(initialUser);
    const [devices, setDevices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [passwords, setPasswords] = useState({
        current: '',
        new: '',
        confirm: ''
    });

    React.useEffect(() => {
        fetchProfile();
        fetchDevices();
    }, []);

    const fetchProfile = async () => {
        try {
            const token = localStorage.getItem('token');
            const resp = await axios.get(`${CONFIG.API_URL}/api/auth/profile/${initialUser.id || initialUser._id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setUser(resp.data);
        } catch (err) {
            console.error('Failed to fetch profile:', err);
        }
    };

    const fetchDevices = async () => {
        try {
            const token = localStorage.getItem('token');
            const resp = await axios.get(`${CONFIG.API_URL}/api/devices`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setDevices(resp.data);
            setLoading(false);
        } catch (err) {
            console.error('Failed to fetch devices:', err);
            setLoading(false);
        }
    };

    const handleUpdatePassword = async (e) => {
        e.preventDefault();
        if (passwords.new !== passwords.confirm) {
            Swal.fire('Error', 'New passwords do not match', 'error');
            return;
        }

        try {
            const token = localStorage.getItem('token');
            await axios.post(`${CONFIG.API_URL}/api/auth/update-password`, {
                userId: user.id || user._id,
                currentPassword: passwords.current,
                newPassword: passwords.new
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            Swal.fire({
                title: 'Success',
                text: 'Password updated successfully',
                icon: 'success',
                confirmButtonColor: 'var(--primary)',
                customClass: { popup: 'premium-swal' }
            });
            setPasswords({ current: '', new: '', confirm: '' });
        } catch (err) {
            Swal.fire({
                title: 'Error',
                text: err.response?.data?.message || 'Failed to update password',
                icon: 'error',
                confirmButtonColor: 'var(--primary)',
                customClass: { popup: 'premium-swal' }
            });
        }
    };

    if (loading) return <LoadingSpinner />;

    return (
        <div>
            <div style={{ marginBottom: '24px' }}>
                <h2 style={{ fontSize: '28px', fontWeight: 800 }}>{user.role === 'ADMIN' ? 'Master Admin' : user.role === 'DOCTOR' ? 'Medical Officer' : 'Run Officer'} Profile</h2>
                <p style={{ color: 'var(--text-muted)' }}>Manage your account settings and {user.role === 'DOCTOR' ? 'digital signature' : 'device configurations'}.</p>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '32px' }}>
                {/* Profile Card */}
                <div className="glass-card">
                    <div style={{ textAlign: 'center', marginBottom: '32px' }}>
                        <div className="profile-avatar" style={{ width: '100px', height: '100px', margin: '0 auto 16px', fontSize: '32px' }}>
                            {user.name?.charAt(0) || 'A'}
                        </div>
                        <h3 style={{ fontSize: '20px', fontWeight: 800 }}>{user.name}</h3>
                        <p style={{ color: 'var(--primary)', fontWeight: 700, fontSize: '12px', textTransform: 'uppercase', letterSpacing: '1px' }}>{user.description || user.designation || user.role}</p>
                    </div>

                    <div style={{ display: 'grid', gap: '20px' }}>
                        <div>
                            <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-muted)', fontWeight: 700, marginBottom: '4px' }}>EMAIL ADDRESS</label>
                            <div style={{ fontSize: '15px', fontWeight: 600 }}>{user.email}</div>
                        </div>
                        <div>
                            <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-muted)', fontWeight: 700, marginBottom: '4px' }}>DESCRIPTION</label>
                            <div style={{ fontSize: '14px', fontWeight: 600 }}>{user.description || user.designation || 'Staff Member'}</div>
                        </div>
                        <div>
                            <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-muted)', fontWeight: 700, marginBottom: '4px' }}>ACCOUNT ID</label>
                            <div style={{ fontSize: '15px', fontWeight: 600, fontFamily: 'monospace' }}>{user.id || user._id}</div>
                        </div>
                        <div>
                            <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-muted)', fontWeight: 700, marginBottom: '4px' }}>LAST LOGIN</label>
                            <div style={{ fontSize: '14px', fontWeight: 600 }}>{user.last_login ? new Date(user.last_login).toLocaleString() : 'N/A'}</div>
                        </div>
                        <div style={{ padding: '16px', background: '#f8fafc', borderRadius: '12px', marginTop: '12px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#10b981', fontWeight: 700, fontSize: '13px', marginBottom: '8px' }}>
                                <Icon icon="ion:shield-checkmark-outline" width="18" /> Verification Active
                            </div>
                            <p style={{ fontSize: '12px', color: 'var(--text-muted)', margin: 0 }}>Two-factor authentication is enabled for this admin account.</p>
                        </div>
                    </div>
                </div>

                {/* Settings Tabs */}
                <div style={{ display: 'grid', gap: '32px' }}>
                    <div className="glass-card">
                        <h3 style={{ fontSize: '18px', fontWeight: 800, marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <Icon icon="ion:key-outline" color="var(--primary)" /> Update Password
                        </h3>
                        <form onSubmit={handleUpdatePassword} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                            <div style={{ gridColumn: 'span 2' }}>
                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, fontSize: '14px' }}>Current Password</label>
                                <input 
                                    type="password" 
                                    className="input-field" 
                                    style={{ width: '100%', padding: '12px', borderRadius: '12px', border: '1px solid var(--border-light)' }} 
                                    value={passwords.current}
                                    onChange={(e) => setPasswords({...passwords, current: e.target.value})}
                                    required
                                />
                            </div>
                            <div>
                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, fontSize: '14px' }}>New Password</label>
                                <input 
                                    type="password" 
                                    className="input-field" 
                                    style={{ width: '100%', padding: '12px', borderRadius: '12px', border: '1px solid var(--border-light)' }} 
                                    value={passwords.new}
                                    onChange={(e) => setPasswords({...passwords, new: e.target.value})}
                                    required
                                />
                            </div>
                            <div>
                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, fontSize: '14px' }}>Confirm Password</label>
                                <input 
                                    type="password" 
                                    className="input-field" 
                                    style={{ width: '100%', padding: '12px', borderRadius: '12px', border: '1px solid var(--border-light)' }} 
                                    value={passwords.confirm}
                                    onChange={(e) => setPasswords({...passwords, confirm: e.target.value})}
                                    required
                                />
                            </div>
                            <div style={{ gridColumn: 'span 2' }}>
                                <button type="submit" className="btn-primary" style={{ width: 'auto', padding: '12px 32px' }}>
                                    Save New Password
                                </button>
                            </div>
                        </form>
                    </div>

                    {user.role === 'DOCTOR' && (
                        <div className="glass-card">
                            <h3 style={{ fontSize: '18px', fontWeight: 800, marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <Icon icon="ion:pencil-outline" color="var(--primary)" /> Digital Signature
                            </h3>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', alignItems: 'center' }}>
                                <div>
                                    <p style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '16px', lineHeight: '1.5' }}>
                                        Upload your digital signature to use it automatically during medical evaluations. This will save you time from drawing it every time.
                                    </p>
                                    <div style={{ display: 'flex', gap: '12px' }}>
                                        <input 
                                            type="file" 
                                            accept="image/*" 
                                            id="signature-upload"
                                            style={{ display: 'none' }}
                                            onChange={async (e) => {
                                                const file = e.target.files[0];
                                                if (file) {
                                                    // Show processing indicator
                                                    Swal.fire({
                                                        title: 'Processing Signature...',
                                                        text: 'Please wait while we secure your digital signature.',
                                                        allowOutsideClick: false,
                                                        didOpen: () => {
                                                            Swal.showLoading();
                                                        }
                                                    });

                                                    const reader = new FileReader();
                                                    reader.onloadend = async () => {
                                                        try {
                                                            const token = localStorage.getItem('token');
                                                            await axios.post(`${CONFIG.API_URL}/api/doctor/signature`, {
                                                                signature_hash: reader.result
                                                            }, {
                                                                headers: { Authorization: `Bearer ${token}` }
                                                            });
                                                            
                                                            setUser({ ...user, signature_hash: reader.result });
                                                            
                                                            // Show success message
                                                            Swal.fire({
                                                                icon: 'success',
                                                                title: 'Signature Linked',
                                                                text: 'Your digital signature has been successfully stored and encrypted.',
                                                                confirmButtonColor: '#800000'
                                                            });
                                                        } catch (err) {
                                                            Swal.fire({
                                                                icon: 'error',
                                                                title: 'Upload Failed',
                                                                text: err.response?.data?.message || 'Failed to securely upload signature.',
                                                                confirmButtonColor: '#800000'
                                                            });
                                                        }
                                                    };
                                                    reader.readAsDataURL(file);
                                                }
                                            }}
                                        />
                                        <label htmlFor="signature-upload" className="btn-primary" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', cursor: 'pointer', padding: '10px 20px', borderRadius: '10px' }}>
                                            <Icon icon="ion:cloud-upload-outline" width="20" /> {user.signature_hash ? 'Update' : 'Upload'}
                                        </label>
                                        
                                        {user.signature_hash && (
                                            <button 
                                                onClick={async () => {
                                                    const res = await Swal.fire({
                                                        title: 'Delete Signature?',
                                                        text: "You will need to re-upload it to use it again.",
                                                        icon: 'warning',
                                                        showCancelButton: true,
                                                        confirmButtonColor: '#800000',
                                                        confirmButtonText: 'Yes, delete it!'
                                                    });
                                                    
                                                    if (res.isConfirmed) {
                                                        try {
                                                            const token = localStorage.getItem('token');
                                                            await axios.delete(`${CONFIG.API_URL}/api/doctor/signature`, {
                                                                headers: { Authorization: `Bearer ${token}` }
                                                            });
                                                            setUser({ ...user, signature_hash: null });
                                                            Swal.fire('Deleted!', 'Your signature has been removed.', 'success');
                                                        } catch (err) {
                                                            Swal.fire('Error', 'Failed to delete signature', 'error');
                                                        }
                                                    }
                                                }}
                                                className="btn-secondary" 
                                                style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '10px 20px', borderRadius: '10px', color: '#ef4444', borderColor: '#fee2e2' }}
                                            >
                                                <Icon icon="ion:trash-outline" width="20" /> Delete
                                            </button>
                                        )}
                                    </div>
                                </div>
                                <div style={{ 
                                    border: '2px dashed var(--border-light)', 
                                    borderRadius: '16px', 
                                    height: '140px', 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'center',
                                    background: '#f8fafc',
                                    overflow: 'hidden',
                                    position: 'relative'
                                }}>
                                    {user.signature_hash ? (
                                        <img src={user.signature_hash} alt="Signature Preview" style={{ maxWidth: '100%', maxHeight: '100%' }} />
                                    ) : (
                                        <div style={{ textAlign: 'center', color: '#cbd5e1' }}>
                                            <Icon icon="ion:image-outline" width="48" style={{ marginBottom: '8px' }} />
                                            <div style={{ fontSize: '12px', fontWeight: 600 }}>No Signature Uploaded</div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {user.role !== 'DOCTOR' && (
                        <div className="glass-card">
                            <h3 style={{ fontSize: '18px', fontWeight: 800, marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <Icon icon="ion:hardware-chip-outline" color="var(--primary)" /> Connected Hardware
                            </h3>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '16px' }}>
                                {devices.length === 0 ? (
                                    <p style={{ color: 'var(--text-muted)', fontSize: '14px' }}>No active devices found.</p>
                                ) : (
                                    devices.map(device => (
                                        <DeviceMiniCard 
                                            key={device.device_id}
                                            name={device.name} 
                                            id={device.device_id} 
                                            status={device.status} 
                                        />
                                    ))
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const DeviceMiniCard = ({ name, id, status }) => (
    <div style={{ padding: '16px', border: '1px solid var(--border-light)', borderRadius: '16px', background: '#f8fafc' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
            <Icon icon="ion:cube-outline" color={status === 'online' ? '#10b981' : '#cbd5e1'} />
            <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: status === 'online' ? '#10b981' : '#ef4444' }}></div>
        </div>
        <div style={{ fontWeight: 700, fontSize: '14px', marginBottom: '2px' }}>{name}</div>
        <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{id}</div>
    </div>
);

export default Profile;
